// Merges the offline queue into a week response so queued changes show up in the week view:
// queued creates become local cards, queued updates change the card of their entry, a queued
// delete keeps the card as "wird gelöscht", submits/withdrawals/corrections mark their target.
// Pure; the hook in `useOfflineQueue.ts` feeds it.
import {
  breakMinutes,
  grossMinutes,
  netMinutes,
  type TimeEntryBodyInput,
  type TimeEntryDetail,
  type WeekResponse,
} from "@zeiterfassung/shared";
import type { QueuedMutation, QueuedMutationKind } from "./offline-queue";

export type QueueRef = { id: string; kind: QueuedMutationKind };
export type MergedEntry = TimeEntryDetail & { queue?: QueueRef };
export type MergedDay = Omit<WeekResponse["days"][number], "entries"> & { entries: MergedEntry[] };
export type MergedWeek = Omit<WeekResponse, "days"> & { days: MergedDay[] };

export function localEntryId(clientId: string): string {
  return `local:${clientId}`;
}

type EntryBody = Omit<TimeEntryBodyInput, "clientId">;

function isEntryBody(body: unknown): body is EntryBody {
  const b = body as Partial<EntryBody> | null;
  return (
    typeof b === "object" &&
    b !== null &&
    typeof b.workDate === "string" &&
    typeof b.startedAt === "string" &&
    typeof b.endedAt === "string"
  );
}

function withTimes<T extends TimeEntryDetail>(entry: T, body: EntryBody): T {
  const breaks = (body.breaks ?? []).map((b, i) => ({ id: `${entry.id}:${i}`, ...b }));
  const interval = { startedAt: body.startedAt, endedAt: body.endedAt, breaks };
  return {
    ...entry,
    assignmentId: body.assignmentId,
    workDate: body.workDate,
    startedAt: body.startedAt,
    endedAt: body.endedAt,
    breaks,
    comment: body.comment ?? null,
    grossMinutes: grossMinutes(interval),
    breakMinutes: breakMinutes(breaks),
    netMinutes: netMinutes(interval),
  };
}

function localEntry(week: WeekResponse, item: QueuedMutation, body: EntryBody): MergedEntry {
  const assignment = week.assignments.find((a) => a.id === body.assignmentId);
  const base: TimeEntryDetail = {
    id: localEntryId(item.clientId ?? item.id),
    zvEmployeeId: week.employee?.zvEmployeeId ?? 0,
    assignmentId: body.assignmentId,
    assignment: assignment
      ? { customerName: assignment.customerName, positionLabel: assignment.positionLabel }
      : null,
    workDate: body.workDate,
    startedAt: body.startedAt,
    endedAt: body.endedAt,
    breaks: [],
    comment: null,
    status: "draft",
    warnings: [],
    assignmentChanged: false,
    rejectionNote: null,
    supersededBy: null,
    supersedes: null,
    correctedInZvoove: false,
    zvProcessed: false,
    retryable: false,
    syncPendingDelete: false,
    syncError: null,
    hasBookings: false,
    grossMinutes: 0,
    breakMinutes: 0,
    netMinutes: 0,
    createdBy: "",
    createdByName: null,
    createdAt: item.createdAt,
    submittedAt: null,
    decidedBy: null,
    decidedByName: null,
    decidedAt: null,
    updatedAt: item.createdAt,
    clientId: item.clientId ?? null,
    history: [],
  };
  return { ...withTimes(base, body), queue: { id: item.id, kind: item.kind } };
}

export function mergeQueueIntoWeek(
  week: WeekResponse,
  queue: readonly QueuedMutation[],
): MergedWeek {
  const days: MergedDay[] = week.days.map((d) => ({ ...d, entries: [...d.entries] }));
  const dayOf = (date: string) => days.find((d) => d.date === date);
  let submitWeekQueued = false;

  const mark = (entryId: string, item: QueuedMutation, patch?: EntryBody) => {
    for (const day of days) {
      const index = day.entries.findIndex((e) => e.id === entryId);
      if (index < 0) continue;
      const current = day.entries[index];
      if (!current) continue;
      const next: MergedEntry = { ...current, queue: { id: item.id, kind: item.kind } };
      day.entries[index] = patch ? withTimes(next, patch) : next;
      return;
    }
  };

  for (const item of queue) {
    switch (item.kind) {
      case "entry_create": {
        if (!isEntryBody(item.body)) break;
        const day = dayOf(item.body.workDate);
        if (!day) break;
        day.entries.push(localEntry(week, item, item.body));
        break;
      }
      case "entry_update": {
        if (!item.entryId || !isEntryBody(item.body)) break;
        mark(item.entryId, item, item.body);
        break;
      }
      case "entry_delete":
      case "entry_correct":
      case "entry_submit":
      case "entry_withdraw": {
        const target = item.entryId ?? (item.clientId ? localEntryId(item.clientId) : undefined);
        if (target) mark(target, item);
        break;
      }
      case "week_submit": {
        const body = item.body as { week?: unknown } | null;
        if (body?.week !== week.week.id) break;
        submitWeekQueued = true;
        for (const day of days) {
          day.entries = day.entries.map((e) =>
            e.status === "draft" && !e.queue
              ? { ...e, queue: { id: item.id, kind: item.kind } }
              : e,
          );
        }
        break;
      }
      default:
        break;
    }
  }

  let net = 0;
  const counts = { ...week.counts, draft: 0 };
  for (const day of days) {
    day.entries.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
    for (const entry of day.entries) {
      const deleting = entry.queue?.kind === "entry_delete";
      if (entry.status !== "rejected" && !deleting) net += entry.netMinutes;
      if (entry.status === "draft" && !entry.queue) counts.draft += 1;
    }
  }
  return {
    ...week,
    days,
    counts,
    totals: { ...week.totals, netMinutes: net },
    canSubmit: week.canSubmit && !submitWeekQueued && counts.draft > 0,
  };
}
